import { useEffect, useRef } from 'react';

import {
  InternalQuestion,
  PlaceholderWithPicture,
  useImageGalleryContext,
  usePicturesContext,
} from '../../../contexts';

export interface GalleryProps {
  question: InternalQuestion;
  showAllPlaceholders?: boolean;
  onChange: (pictures: any[]) => void;
}

export const useGallery = ({ question, showAllPlaceholders, onChange }: GalleryProps) => {
  const selectedPlaceholderName = useRef('');
  const { getGalleryPictures, getPlaceholdersWithoutPicture } = useImageGalleryContext();
  const { pictures, getQuestionPictures } = usePicturesContext();

  const {
    pictures: placeholdersWithPictures,
    genericPictures,
    index: lastIndexWithPicture,
  } = getGalleryPictures(question);

  const calculateGenericPlaceHolders = (files: any) => {
    let placeholders = [selectedPlaceholderName.current];
    if (files?.length > 1) {
      placeholders = getPlaceholdersWithoutPicture(question).map((placeholder) => placeholder.placeholderName);

      /**
       * TODO: Calculate GENERIC placeholders needed
       * This is assuming placeholders is an
       * empty array
       */
      for (const i in files) {
        placeholders.push(`GENERIC-${genericPictures.length + parseInt(i, 10) + 1}`);
      }
    }
    return placeholders;
  };

  useEffect(() => {
    const questionPictures = getQuestionPictures(pictures, question.key);
    onChange(questionPictures);
  }, [pictures]);

  const questionPictures = getQuestionPictures(pictures, question.key);
  const questionPlaceholdersLength = question.imagesPlaceholders?.length ? question.imagesPlaceholders?.length : 0;

  /**
   * selectedPicture is used to set the gallerySelectedPicture
   * on ImageGalleryContext so a view like 'Camera' has easy
   * access to it.
   */
  const selectedPicture = useRef<PlaceholderWithPicture | null>(null);

  /**
   * TODO: replace 1000 for proper number
   * 1000 is big enough to trim the array
   * for what we need but this number should be
   * calculated and not hardcoded
   */
  !showAllPlaceholders && placeholdersWithPictures.splice(lastIndexWithPicture + 2, 1000);

  const addNewPlaceHolder = (customIndex?: number) => {
    const placeholderName = `GENERIC-${customIndex || genericPictures.length + 1}`;
    selectedPlaceholderName.current = placeholderName;
    selectedPicture.current = {
      picture: null,
      placeholderName: placeholderName,
      placeholderUrl: '',
      placeholderUrlForCamera: '',
    };
  };

  const selectFirstEmptyPlaceHolder = () => {
    const placeholder = getPlaceholdersWithoutPicture(question)?.[0];
    if (placeholder) {
      selectedPlaceholderName.current = placeholder.placeholderName;
      selectedPicture.current = {
        picture: null,
        placeholderName: placeholder.placeholderName,
        placeholderUrl: placeholder.placeholderUrl,
        placeholderUrlForCamera: placeholder.placeholderUrlForCamera,
      };
    }
  };

  return {
    questionPictures,
    questionPlaceholdersLength,
    selectedPicture,
    placeholdersWithPictures,
    selectedPlaceholderName,
    calculateGenericPlaceHolders,
    addNewPlaceHolder,
    selectFirstEmptyPlaceHolder,
  };
};
